// src/components/programs/ProgramForm/helpers/formInitializer.js

import { INITIAL_FORM_DATA, EMPTY_HOTEL_TEMPLATE, HOTEL_PRICING_MODES } from "./formConstants";
import { generateDayBoxes, addDays } from "../../../../utils/programs/dateCalculations";
import { extractPricingUnitCode } from "../Step1Sections/utils/pricingUnitHelper";

const mergeProductWithDetails = (product, allDetails) => {
    const detail = allDetails.find(d => d.product_id === product.product_id);
    if (!detail) return null;
    return {
        product_id: product.product_id,
        product_data: product,
        detail_data: detail,
    };
};

const buildDynamicFields = (product) => {
    const fields = {};
    if (product.time !== null && product.time !== undefined) fields.time = product.time;
    if (product.pickup_time !== null && product.pickup_time !== undefined) fields.pickup_time = product.pickup_time;
    if (product.city_id !== null && product.city_id !== undefined) fields.city_id = product.city_id;
    if (product.units !== null && product.units !== undefined) fields.units = product.units;
    if (product.nights !== null && product.nights !== undefined) fields.nights = product.nights;
    if (product.flight_details !== null && product.flight_details !== undefined) {
        fields.flight_details = { ...product.flight_details };
    }
    return fields;
};

const buildRegularProduct = (product_data, detail_data, pricingUnitCode) => {
    const originalSellPrice = parseFloat(detail_data.original_sell_price) ||
        parseFloat(detail_data.product?.price) || 0;
    const originalCostPrice = parseFloat(detail_data.cost_price) ||
        parseFloat(detail_data.product?.cost_price) || 0;
    const customPrice = detail_data.custom_price
        ? parseFloat(detail_data.custom_price)
        : null;
    const currentPrice = customPrice !== null ? customPrice : originalSellPrice;
    const isPriceEdited = customPrice !== null && customPrice !== originalSellPrice;

    return {
        instance_id: product_data.id || `temp_${Date.now()}_${Math.random()}`,
        product_snapshot: {
            product_id: product_data.product_id || product_data.id,
            title: product_data.title || detail_data.product?.title || { ar: '', en: '' },
            category: product_data.category || detail_data.product?.category || null,
            category_id: product_data.category_id || detail_data.product?.category_id || null,
            product_type_id: product_data.product_type_id || detail_data.product?.product_type_id || null,
            pricing_unit_id: product_data.pricing_unit_id || product_data.pricingunitid || detail_data.product?.pricing_unit_id || null,
            pricing_unit: pricingUnitCode,
            productType: product_data.productType || product_data.product_type || detail_data.product?.productType || detail_data.product?.product_type || null,
            product_type: product_data.product_type || product_data.productType || detail_data.product?.product_type || detail_data.product?.productType || null,
            original_price: originalSellPrice,
            original_cost_price: originalCostPrice,
            original_sell_price: originalSellPrice,
            pax_type_prices: (detail_data.pax_type_prices || detail_data.product?.pax_type_prices || []).map(p => ({
                pax_type_id: parseInt(p.pax_type_id),
                pax_type_name: p.pax_type_name || null,
                price: parseFloat(p.price) || 0,
                cost_price: parseFloat(p.cost_price) || 0,
                custom_cost_price: p.custom_cost_price != null ? parseFloat(p.custom_cost_price) : null,
            })),
        },
        selected_supplier: detail_data.supplier || null,
        selected_service: detail_data.service || null,
        order: product_data.order || 1,
        pricing: {
            price: currentPrice,
            cost_price: originalCostPrice,
            custom_price: customPrice,
            custom_cost_price: detail_data.custom_cost_price != null
                ? parseFloat(detail_data.custom_cost_price)
                : null,
            is_price_edited: isPriceEdited || (detail_data.custom_cost_price != null),
            discount_type: detail_data.discount_type || 'zero',
            discount_value: parseFloat(detail_data.discount) || 0,
            original_sell_price: originalSellPrice,
            custom_pax_prices: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                if (ptp.custom_price != null) acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_price);
                return acc;
            }, {}),
            custom_pax_costs: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                if (ptp.custom_cost_price != null) acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_cost_price);
                return acc;
            }, {}),
        },
        dynamic_fields: buildDynamicFields(product_data),
        calculated: {
            final_price_per_unit: parseFloat(detail_data.final_price) || currentPrice,
            final_cost_per_unit: originalCostPrice,
            quantity: 0,
            total_price: 0,
            total_cost: 0,
        },
    };
};

const extractHotel = (product_data, detail_data, dayDate) => {
    const hotelDetails = product_data.hotel_details || {};
    const nights = parseInt(product_data.nights) || 1;
    const checkInDate = dayDate;
    const checkOutDate = addDays(checkInDate, nights);
    const pricingMode = product_data.pricing_mode || hotelDetails.pricing_mode || '';

    const isRoomNightDaily = pricingMode === HOTEL_PRICING_MODES.ROOM_NIGHT_DAILY ||
        (hotelDetails.room_stays && hotelDetails.room_stays.length > 0);

    if (isRoomNightDaily) {
        // NEW format: room_night_daily
        return {
            ...EMPTY_HOTEL_TEMPLATE,
            id: `hotel_migrated_${Date.now()}_${Math.random()}`,
            check_in_date: checkInDate,
            check_out_date: checkOutDate,
            nights: nights,
            hotel_name: hotelDetails.hotel_name || '',
            country_id: hotelDetails.country_id || null,
            city_id: hotelDetails.city_id || null,
            check_in_time: hotelDetails.check_in_time || '14:00',
            check_out_time: hotelDetails.check_out_time || '12:00',
            meal_plan_id: hotelDetails.meal_plan_id || null,
            hotel_rating_id: hotelDetails.hotel_rating_id || null,
            product_snapshot: {
                product_id: detail_data.product_id || product_data.product_id,
                title: detail_data.product?.title || product_data.title || { ar: '', en: '' },
                pricing_unit: HOTEL_PRICING_MODES.ROOM_NIGHT_DAILY,
                original_sell_price: 0,
                pax_type_prices: [],
            },
            selected_supplier: detail_data.supplier || null,
            room_stays: (hotelDetails.room_stays || []).map(rs => ({
                id: `rs_${Date.now()}_${Math.random()}`,
                date: rs.date,
                room_category_id: rs.room_category_id || null,
                rooms_count: rs.rooms_count || 1,
                base_sell_price: 0,
                base_cost_price: 0,
                custom_sell_price: rs.custom_sell_price ?? null,
                custom_cost_price: rs.custom_cost_price ?? null,
                extras: (hotelDetails.extras || [])
                    .filter(ex => (ex.applicable_dates || []).includes(rs.date))
                    .map(ex => ({
                        id: `ext_${Date.now()}_${Math.random()}`,
                        hotel_extra_id: ex.hotel_extra_id || null,
                        name: ex.name || ex.extra_name || null,
                        pricing_basis: ex.pricing_basis || 'once',
                        quantity: parseInt(ex.quantity) || 1,
                        custom_sell_price: parseFloat(ex.custom_sell_price ?? ex.sell_price) || 0,
                        custom_cost_price: parseFloat(ex.custom_cost_price ?? ex.cost_price) || 0,
                    })),
            })),
        };
    }

    // LEGACY format: per_person_night
    return {
        ...EMPTY_HOTEL_TEMPLATE,
        id: `hotel_migrated_${Date.now()}_${Math.random()}`,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        nights: nights,
        rooms: parseInt(product_data.units) || 1,
        hotel_name: hotelDetails.hotel_name || '',
        country_id: hotelDetails.country_id || null,
        city_id: hotelDetails.city_id || null,
        check_in_time: hotelDetails.check_in_time || '14:00',
        check_out_time: hotelDetails.check_out_time || '12:00',
        meal_plan_id: hotelDetails.meal_plan_id || null,
        hotel_rating_id: hotelDetails.hotel_rating_id || null,
        room_category_id: hotelDetails.room_category_id || null,
        product_snapshot: {
            product_id: detail_data.product_id || product_data.product_id,
            title: detail_data.product?.title || product_data.title || { ar: '', en: '' },
            pricing_unit: 'per_person_night',
            original_sell_price: parseFloat(detail_data.original_sell_price || detail_data.product?.price || 0),
            pax_type_prices: detail_data.pax_type_prices || detail_data.product?.pax_type_prices || [],
        },
        selected_supplier: detail_data.supplier || null,
        pricing: {
            is_price_edited: (detail_data.pax_type_prices || []).some(ptp => ptp.custom_price != null || ptp.custom_cost_price != null) ||
                             (hotelDetails.room_stays || []).some(rs => rs.custom_sell_price != null || rs.custom_cost_price != null) ||
                             (hotelDetails.extras || []).some(ex => ex.custom_sell_price != null || ex.custom_cost_price != null),
            custom_pax_prices: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                if (ptp.custom_price != null) acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_price);
                return acc;
            }, {}),
            custom_pax_costs: (detail_data.pax_type_prices || []).reduce((acc, ptp) => {
                if (ptp.custom_cost_price != null) acc[parseInt(ptp.pax_type_id)] = parseFloat(ptp.custom_cost_price);
                return acc;
            }, {}),
            custom_room_prices: (hotelDetails.room_stays || []).reduce((acc, rs, idx) => {
                if (rs.custom_sell_price != null) acc[idx] = parseFloat(rs.custom_sell_price);
                return acc;
            }, {}),
            custom_room_costs: (hotelDetails.room_stays || []).reduce((acc, rs, idx) => {
                if (rs.custom_cost_price != null) acc[idx] = parseFloat(rs.custom_cost_price);
                return acc;
            }, {}),
            custom_extra_prices: (hotelDetails.extras || []).reduce((acc, ex, idx) => {
                acc[idx] = {
                    sell_price: ex.custom_sell_price != null ? parseFloat(ex.custom_sell_price) : 0,
                    cost_price: ex.custom_cost_price != null ? parseFloat(ex.custom_cost_price) : 0,
                };
                return acc;
            }, {}),
        },
    };
};

const migrateOldExtras = (hotelDetails, firstDayExtras) => {
    if (!hotelDetails.extras || !Array.isArray(hotelDetails.extras)) return;

    hotelDetails.extras.forEach(extra => {
        const rawName = extra.name || extra.extra_name || extra.extra_details?.name || null;
        firstDayExtras.push({
            id: `ext_migrated_${Date.now()}_${Math.random()}`,
            hotel_extra_id: extra.hotel_extra_id || null,
            name: rawName,
            pricing_basis: extra.pricing_basis || 'once',
            quantity: parseInt(extra.quantity) || 1,
            days_count: extra.days_count || null,
            custom_sell_price: parseFloat(extra.custom_sell_price ?? extra.sell_price) || 0,
            custom_cost_price: parseFloat(extra.custom_cost_price ?? extra.cost_price) || 0,
        });
    });
};

/**
 * Initialize form data from existing program data (for edit mode)
 */
export const initializeFormData = (programData) => {
    if (!programData) return INITIAL_FORM_DATA;

    const paxTypes = programData.pax_types?.map((pt) => ({
        pax_type_id: pt.pax_type_id || pt.id,
        pax_type_name: pt.name || pt.pax_type_name || pt.pax_type?.name || { ar: '', en: '' },
        count: parseInt(pt.quantity) || 0,
    })) || [];

    const allDetails = programData.details || [];
    const extractedHotels = [];
    const itinerary = [];

    programData.days?.forEach((day) => {
        const nonHotelProducts = [];
        let hotelProductData = null;
        let hotelDetailData = null;

        day.products?.forEach((product) => {
            const merged = mergeProductWithDetails(product, allDetails);
            if (!merged) return;

            const { product_data, detail_data } = merged;
            const pricingUnitCode = extractPricingUnitCode(product_data) ||
                extractPricingUnitCode(detail_data.product);
            const pricingMode = product_data.pricing_mode || detail_data.product?.pricing_mode || '';
            const isHotelProduct = pricingUnitCode === 'per_person_night' ||
                pricingMode === HOTEL_PRICING_MODES.ROOM_NIGHT_DAILY ||
                product_data.hotel_details?.room_stays ||
                detail_data.hotel_details?.room_stays;

            if (isHotelProduct) {
                hotelProductData = product_data;
                hotelDetailData = detail_data;
            } else {
                nonHotelProducts.push(buildRegularProduct(product_data, detail_data, pricingUnitCode));
            }
        });

        const dayEntry = {
            date: day.date,
            day_name: day.day_name || { ar: '', en: '' },
            products: nonHotelProducts,
            extras: [],
        };

        itinerary.push(dayEntry);

        // Extract hotel product found on this day
        if (hotelProductData && hotelDetailData) {
            const hotel = extractHotel(hotelProductData, hotelDetailData, day.date);
            extractedHotels.push(hotel);

            // Migrate old-style hotel_details.extras to per-day extras on this day
            migrateOldExtras(hotelProductData.hotel_details || {}, dayEntry.extras);
        }
    });

    // Generate empty days if no days exist
    if (itinerary.length === 0 && programData.start_date && programData.durations) {
        const emptyDays = generateDayBoxes(
            programData.start_date,
            parseInt(programData.durations) || 0
        );
        itinerary.push(...emptyDays.map(d => ({ ...d, extras: [] })));
    }

    return {
        title: programData.title || { ar: '', en: '' },
        description: programData.description || { ar: '', en: '' },
        agent_id: programData.agent_id || '',
        city_ids: programData.cities ? programData.cities.map(city => city.id) : [],
        start_date: programData.start_date || '',
        end_date: programData.end_date || '',
        durations_days: parseInt(programData.durations) || 0,
        durations_nights: parseInt(programData.nights) || 0,
        show_date_in_template: !!programData.show_date_in_template,
        cover_image: programData.cover_image?.file_name || null,
        cover_image_preview: programData.cover_image?.url || programData.cover_image?.preview || '',
        pax_types: paxTypes,
        hotels: extractedHotels,
        itinerary: itinerary,
        global_discount: {
            type: programData.discount_type || 'zero',
            value: parseFloat(programData.discount) || 0,
            target: programData.discount_target || 'none',
        },
        uploadingCoverImage: false,
        coverImageError: '',
        coverImageSuccess: '',
    };
};
